// One-shot attempt-marking observability — one row per completed attempt marked
// against its uploaded mark-scheme document.
//
// Reads the DEDICATED `mark-attempt-runs` container (OperationRun records), not
// `async-jobs`. See docs/FeatureDesigns/operation-run-records.md.
//
// The "Cross-check" column is why this list exists rather than being a filter on
// Model Runs. Marking fails SILENTLY: a misread scheme awards plausible, wrong
// marks and the run still reports success. Σ of the tariffs the model returned
// vs the Maximum Mark printed on the scheme's cover is the one document-supplied
// invariant that catches it, so it is surfaced at list level — not buried in a
// detail page nobody opens on a green run.
import { useMemo, useState } from 'react';
import { useOpenOperationRow } from './OperationNav';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { adminApi, type MarkAttemptRunView } from '../../api/adminApi';
import { DataTable, Badge, type ColumnDef } from '@shared-ui/components';

const STATUS_BADGE: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  succeeded: 'success',
  failed: 'error',
  running: 'warning',
};

const formatLatency = (ms?: number) => (ms ? `${(ms / 1000).toFixed(1)}s` : '—');
const formatCost = (c?: number) => (typeof c === 'number' ? `$${c.toFixed(4)}` : '—');

export function MarkAttemptList() {
  const [status, setStatus] = useState('');
  const openRow = useOpenOperationRow();

  const { data, isLoading, error } = useQuery({
    queryKey: ['markAttemptRuns', status],
    queryFn: () => adminApi.getMarkAttemptRuns({ status: status || undefined, limit: 50 }),
  });

  const rows = data?.items ?? [];

  const columns = useMemo<ColumnDef<MarkAttemptRunView>[]>(() => [
    {
      key: 'started',
      header: 'Started',
      render: (r) => format(new Date(r.startedAt), 'MMM d, HH:mm:ss'),
    },
    {
      key: 'student',
      header: 'Student',
      render: (r) => (
        <div>
          <div>{r.studentName || '—'}</div>
          {r.studentEmail && (
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{r.studentEmail}</div>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <Badge variant={STATUS_BADGE[r.status] ?? 'default'} size="sm">
          {r.status}
        </Badge>
      ),
    },
    {
      key: 'marks',
      header: 'Marks',
      render: (r) => (r.outcome ? `${r.outcome.marksAwarded}/${r.outcome.maxMarks}` : '—'),
    },
    {
      key: 'coverage',
      header: 'Questions',
      render: (r) => {
        const o = r.outcome;
        if (!o) return '—';
        const missed = o.unreturnedIndices?.length ?? 0;
        return (
          <div>
            <div>{o.questionsMarked} marked</div>
            <div style={{ fontSize: 12, color: missed ? 'var(--color-error, #c2321f)' : 'var(--text-tertiary)' }}>
              {o.questionsWithCredits} with credits
              {missed > 0 && ` · ${missed} unreturned`}
            </div>
          </div>
        );
      },
    },
    {
      key: 'crosscheck',
      header: 'Cross-check',
      render: (r) => {
        const o = r.outcome;
        if (!o) return '—';
        if (o.statedMaximumMark == null) {
          // No cover total in the document ⇒ no check was possible. Say so
          // rather than showing a bare sum that looks verified.
          return (
            <div>
              <div>Σ {o.tariffSum}</div>
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>no stated max</div>
            </div>
          );
        }
        return (
          <Badge variant={o.totalsAgree ? 'success' : 'error'} size="sm">
            Σ {o.tariffSum} / {o.statedMaximumMark}
          </Badge>
        );
      },
    },
    {
      key: 'model',
      header: 'Model / tokens',
      render: (r) =>
        r.llm ? (
          <div>
            <div>{r.llm.provider || '—'}</div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              {r.llm.inputTokens}→{r.llm.outputTokens} · {formatCost(r.llm.costUsd)}
            </div>
          </div>
        ) : (
          '—'
        ),
    },
    {
      key: 'latency',
      header: 'Latency',
      render: (r) => formatLatency(r.latencyMs),
    },
    {
      key: 'error',
      header: 'Error',
      render: (r) =>
        r.error ? (
          <span style={{ color: 'var(--color-error, #c2321f)', fontSize: 12 }}>{r.error}</span>
        ) : (
          '—'
        ),
    },
  ], []);

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <label style={{ fontSize: 13 }}>
          Status:{' '}
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">All</option>
            <option value="succeeded">Succeeded</option>
            <option value="failed">Failed</option>
            <option value="running">Running</option>
          </select>
        </label>
      </div>

      {error ? (
        <div style={{ color: 'var(--color-error, #c2321f)' }}>Failed to load marking runs.</div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          loading={isLoading}
          rowKey={(r) => r.id}
          onRowClick={(r) => openRow(rows, r, '/markattempt')}
          emptyMessage="No marking runs yet."
        />
      )}
    </div>
  );
}
